import {
  Wrap,
  WrapItem,
  Center,
  Spacer,
  MenuButton,
  Button,
  MenuList,
  MenuItem,
  Text,
  Menu,
  Flex,
  Box,
  IconButton,
  useDisclosure,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  VStack,
  HStack,
} from "@chakra-ui/react";
import { Link, LinkProps } from "react-router-dom";
import { ChevronDownIcon, MenuIcon } from "lucide-react";
import { Login } from "./login";
import { useEffect, useState } from "react";

////////////////////////////////////////////////////////////////////////////////
//   TYPES
////////////////////////////////////////////////////////////////////////////////
export interface MenuProps {
  appName: string;
  content: Array<MenuLink | MenuLinkGroup>;
}

export interface MenuLink {
  name: string;
  to?: LinkProps["to"];
}

export interface MenuLinkGroup {
  name: string;
  links: Array<MenuLink>;
}

export function isMenuGroup(obj: any): obj is MenuLinkGroup {
  return (
    typeof obj === "object" && obj !== null && "name" in obj && "links" in obj
  );
}

////////////////////////////////////////////////////////////////////////////////
//   Menu implementation
////////////////////////////////////////////////////////////////////////////////
export function TopMenu(props: MenuProps) {
  const [currentTab, setCurrentTab] = useState<number | undefined>(undefined);
  const [currentListItem, setCurrentListItem] = useState<number | undefined>(
    undefined
  );
  const { isOpen, onOpen, onClose } = useDisclosure();

  return (
    <Flex align="center" width="100%" justify="space-between">
      {/* App Name */}
      <Box>
        <Text
          fontSize={{ base: "20px", md: "32px" }}
          color="white"
          fontWeight="bold"
        >
          {props.appName}
        </Text>
      </Box>

      {/* Desktop Menu */}
      <Flex display={{ base: "none", md: "flex" }} align="center" flexWrap="wrap">
        {props.content.map((item, itemIndex) => {
          if (isMenuGroup(item)) {
            return (
              <Menu key={"menu" + item.name}>
                <MenuButton
                  as={Button}
                  px={4}
                  py={2}
                  transition="all 0.2s"
                  _expanded={{ bg: "blue.300", color: "black" }}
                  rightIcon={<ChevronDownIcon />}
                  fontSize={{ base: "16px", md: "24px" }}
                  variant="ghost"
                  color={currentTab === itemIndex ? "blue.100" : "white"}
                  borderWidth={currentTab === itemIndex ? "2px" : undefined}
                  _hover={{
                    color: "black",
                    background: "blue.300",
                  }}
                  mr={2}
                >
                  {item.name}
                </MenuButton>
                <MenuList>
                  {item.links.map((i, listItemIndex) => {
                    return (
                      <MenuItem
                        isDisabled={i.to == null}
                        to={i.to}
                        as={Link}
                        key={"menu_item_links_" + i.name}
                        fontSize="20px"
                        background={
                          currentTab === itemIndex &&
                          listItemIndex === currentListItem
                            ? "blue.100"
                            : undefined
                        }
                        onClick={() => {
                          setCurrentTab(itemIndex);
                          setCurrentListItem(listItemIndex);
                        }}
                        _hover={{
                          background: "gray.200",
                          fontSize: "26px",
                        }}
                      >
                        <Text
                          textDecoration={
                            i.to == null ? "line-through" : undefined
                          }
                        >
                          {i.name}
                        </Text>
                      </MenuItem>
                    );
                  })}
                </MenuList>
              </Menu>
            );
          } else {
            return (
              <Button
                to={item.to}
                as={Link}
                fontSize={{ base: "16px", md: "24px" }}
                variant="ghost"
                key={"btn" + item.name}
                borderRadius="10px"
                isDisabled={item.to == null}
                onClick={() => {
                  setCurrentTab(itemIndex);
                }}
                color={currentTab === itemIndex ? "blue.100" : "white"}
                borderWidth={currentTab === itemIndex ? "2px" : undefined}
                _hover={{
                  color: "black",
                  background: "blue.300",
                }}
                mr={2}
              >
                <Text
                  textDecoration={item.to == null ? "line-through" : undefined}
                >
                  {item.name}
                </Text>
              </Button>
            );
          }
        })}
      </Flex>

      {/* Mobile Menu Button */}
      <IconButton
        display={{ base: "flex", md: "none" }}
        icon={<MenuIcon />}
        onClick={onOpen}
        variant="ghost"
        color="white"
        _hover={{ bg: "blue.300", color: "black" }}
        aria-label="Open menu"
      />

      {/* Mobile Drawer */}
      <Drawer isOpen={isOpen} placement="right" onClose={onClose}>
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader>{props.appName}</DrawerHeader>
          <DrawerBody>
            <VStack align="stretch" spacing={4}>
              {props.content.map((item, itemIndex) => {
                if (isMenuGroup(item)) {
                  return (
                    <Box key={"mobile-menu" + item.name}>
                      <Text fontSize="18px" fontWeight="bold" mb={2}>
                        {item.name}
                      </Text>
                      <VStack align="stretch" pl={4}>
                        {item.links.map((i, listItemIndex) => (
                          <Button
                            key={"mobile-menu_item_" + i.name}
                            as={Link}
                            to={i.to}
                            variant="ghost"
                            justifyContent="flex-start"
                            fontSize="16px"
                            isDisabled={i.to == null}
                            onClick={() => {
                              setCurrentTab(itemIndex);
                              setCurrentListItem(listItemIndex);
                              onClose();
                            }}
                            textDecoration={
                              i.to == null ? "line-through" : undefined
                            }
                          >
                            {i.name}
                          </Button>
                        ))}
                      </VStack>
                    </Box>
                  );
                } else {
                  return (
                    <Button
                      key={"mobile-btn" + item.name}
                      as={Link}
                      to={item.to}
                      variant="ghost"
                      justifyContent="flex-start"
                      fontSize="16px"
                      isDisabled={item.to == null}
                      onClick={() => {
                        setCurrentTab(itemIndex);
                        onClose();
                      }}
                      textDecoration={
                        item.to == null ? "line-through" : undefined
                      }
                    >
                      {item.name}
                    </Button>
                  );
                }
              })}
            </VStack>
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      {/* Login */}
      <Box ml={4}>
        <Login />
      </Box>
    </Flex>
  );
}
